import React, { useMemo } from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DisbursementRow, QueryResponse } from '../../../services/pw933bApi';

const { Text } = Typography;

interface ResultTableProps {
  result: QueryResponse;
}

const formatYearMonth = (ym: string): string => {
  if (ym && ym.length === 6) {
    return `${ym.substring(0, 4)} 年 ${ym.substring(4, 6)} 月`;
  }
  return ym || '';
};

const columns: ColumnsType<DisbursementRow & { _key: string }> = [
  {
    title: '員工代號',
    dataIndex: 'empCode',
    key: 'empCode',
    width: 90,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '員工姓名',
    dataIndex: 'empName',
    key: 'empName',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 70,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '銀行名稱',
    dataIndex: 'bankName',
    key: 'bankName',
    width: 120,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '銀行帳號',
    dataIndex: 'bankAcct',
    key: 'bankAcct',
    width: 150,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '薪資加項',
    dataIndex: 'grossPay',
    key: 'grossPay',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '薪資減項',
    dataIndex: 'deduction',
    key: 'deduction',
    width: 100,
    align: 'right',
    render: (v: number) => {
      const val = v ?? 0;
      return <Text type={val > 0 ? 'danger' : undefined}>{val.toLocaleString()}</Text>;
    },
  },
  {
    title: '印領本薪',
    dataIndex: 'printPay',
    key: 'printPay',
    width: 100,
    align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString(),
  },
  {
    title: '實發薪資',
    dataIndex: 'netPay',
    key: 'netPay',
    width: 120,
    align: 'right',
    render: (v: number) => {
      const val = v ?? 0;
      return <Tag color={val > 0 ? 'green' : val < 0 ? 'red' : undefined}>
        {val.toLocaleString()}
      </Tag>;
    },
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const tableData = useMemo(
    () =>
      (result.rows || []).map((row, i) => ({
        ...row,
        _key: `${row.empCode}-${i}`,
      })),
    [result.rows]
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Text strong>
          {formatYearMonth(result.yearMonth)} 撥款清冊 ({result.totalCount} 筆)
        </Text>
        <Text strong>
          撥款總額: <Text type="success">{(result.totalPay ?? 0).toLocaleString()}</Text>
        </Text>
      </div>
      <Table
        columns={columns}
        dataSource={tableData}
        rowKey="_key"
        size="small"
        scroll={{ x: 900, y: 500 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5} align="right">
                <Text strong>合計</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <Text strong>
                  {(result.rows || []).reduce((sum, r) => sum + (r.grossPay ?? 0), 0).toLocaleString()}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text strong type="danger">
                  {(result.rows || []).reduce((sum, r) => sum + (r.deduction ?? 0), 0).toLocaleString()}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <Text strong>
                  {(result.rows || []).reduce((sum, r) => sum + (r.printPay ?? 0), 0).toLocaleString()}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                <Text strong type="success">
                  {(result.totalPay ?? 0).toLocaleString()}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default ResultTable;
